/* -------------------------------------------------------------------------- */
/* --------------------------- THEME : FRAMEWORKS --------------------------- */

// FRAMEWORKS : NODES_MODULES
import Swiper, { Autoplay, Navigation, Parallax, Scrollbar } from "swiper";

import Sketch from "./gems";

// FRAMEWORKS : GSAP BUSINESS
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import ScrollSmoother from "gsap/ScrollSmoother";

gsap.registerPlugin(ScrollTrigger, SplitText, ScrollSmoother);

/* -------------------------------------------------------------------------- */
/* ------------------------ THEME : GLOBAL VARIABLES ------------------------ */

const gems = new Sketch({
	dom: document.querySelector(".webgl"),
	isLockedBySezam: document.querySelector(".home-hero"),
	isHome: document.querySelector(".home-hero"),
});

// ScrollSmoother (GSAP)
let scrollSmooth = ScrollSmoother.create({
	wrapper: "#page-wrapper",
	content: "#page-main",
	smooth: 1,
	smoothTouch: false,
	effects: window.innerWidth < 992 ? false : true,
	normalizeScroll: false,
	ignoreMobileResize: true,
});

scrollSmooth.scrollTo(0);

// Élèment à animer (en CSS ou JS)
const elementsToAnimated = document.querySelectorAll([
	// global
	"[data-animation]",
	".wrapper",
	".home-banner",
	// elementor
	".elementor-button-wrapper",
	".page__article .elementor-heading-title",
	".page__article .elementor-icon-list-item",
	".page__article .elementor-icon-wrapper",
	".elementor-sitemap-section > *",
	".elementor-heading-title.elementor-size-small",
	".elementor-widget-html",
	".elementor-widget-image",
	".elementor-widget-nav-menu",
	".elementor-widget-text-editor",
	".elementor-widget-video",
	".elementor-posts-container .elementor-post",
	".elementor-heading-title.elementor-size-medium",
	"#page-article .section__background",
	".sf",
	".elementor-shortcode",
]);
const textsToAnimated = document.querySelectorAll([
	// global
	"[data-animation='reveal-text']",
	".page__article .elementor-heading-title",
]);

/* -------------------------------------------------------------------------- */
/* ------------------------- THEME : GLOBAL EVENTS -------------------------- */

// DOCUMENT READY :
document.addEventListener("DOMContentLoaded", () => {
	// remove 'no-js' body class
	document.body.classList.remove("no-js");
	// Preloader (et gestion des animations GSAP)
	gsapPreLoader();
	// Gestion des menus
	headerScrolledMenu(160);
	headerMobileMenu();
	menuItemDropdown();
	// Gestion de la bannière
	bannerClosing();
	bannerScrollingDetect();
	// Gestion des "data" custom
	popupInit();
	toggleInit();
	// Gestion des sliders
	allSwiperInit();
	// Gestion du cache
	sessionStorage.setItem("visited", true);
});

// DOCUMENT RESIZED
ScrollTrigger.addEventListener("refreshInit", () => {
	// Actualisation du smooth scroll
	gsapScrollSmootherState("refresh");
	// Actualisation de la bannière
	bannerScrollingDetect();
});

/* -------------------------------------------------------------------------- */
/* ----------------------- THEME : GLOBAL FUNCTIONS ------------------------- */

// Détection du scroll
function headerScrolledMenu(scrollOffset) {
	let position = window.pageYOffset;

	if (position > scrollOffset) {
		document.body.classList.add("page-scrolled");
	} else {
		document.body.classList.remove("page-scrolled");
	}

	document.addEventListener("scroll", () => {
		// detect scroll direction
		let scroll = window.pageYOffset;
		if (scroll > position) {
			// if scroll to bottom
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
				document.body.classList.remove("page-scrolling");
			} else {
				document.body.classList.add("page-scrolling");
				document.body.classList.add("page-scrolled");
			}
		}
		// if scroll to top
		else {
			document.body.classList.remove("page-scrolling");
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
			}
		}
		// if scroll max reached
		if (scroll + window.innerHeight >= document.documentElement.scrollHeight) {
			document.body.classList.add("page-scroll-completed");
		} else {
			document.body.classList.remove("page-scroll-completed");
		}
		position = scroll;
	});
}

// Menu Burger : Comportement au clic
function headerMobileMenu() {
	const pageHeaderBurger = document.querySelector("#page-header-burger");
	const mainMenu = document.querySelector("#main-menu");

	const menuTl = gsap.timeline({ paused: true }).to(mainMenu, {
		clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0 100%)",
		delay: 1,
	});

	pageHeaderBurger.addEventListener("click", () => {
		mainMenu.classList.add("open");
		menuTl.play();
		document.documentElement.style.overflowY = "hidden";
		if (scrollSmooth) {
			gsapScrollSmootherState("pause");
		}
		gems.openMenuScene();
		gsap.to(".page__main > *", { opacity: 0 });
	});

	const pageMenuClose = document.querySelector(".main-menu__close");
	pageMenuClose.addEventListener("click", () => {
		gsap.to(".page__main > *", {
			opacity: 1,
			duration: 1,
			onComplete: function () {
				gems.closeMenuScene();
			},
		});
		mainMenu.classList.remove("open");
		menuTl.reverse();
		document.documentElement.style.overflowY = "auto";
		if (scrollSmooth) {
			gsapScrollSmootherState("play");
		}
	});
}

// Menu Dropdown : Comportement au clic
function menuItemDropdown() {
	const menuItemLinks = document.querySelectorAll(
		'.menu .menu__item .menu__link[href="#"]'
	);

	menuItemLinks.forEach((item) => {
		item.addEventListener("click", (event) => {
			event.preventDefault();
			item.closest(".menu__item").classList.toggle("menu__item--active");
		});
	});

	document.addEventListener("mouseup", (event) => {
		const activeMenuItems = document.querySelectorAll(
			".menu .menu__item.menu__item--active"
		);

		activeMenuItems.forEach((activeItem) => {
			if (
				!activeItem.isEqualNode(event.target) &&
				!activeItem.contains(event.target)
			) {
				activeItem.classList.remove("menu__item--active");
			}
		});
	});
}

// Ouverture/Fermeture de la bannière
function bannerClosing() {
	const pageBanner = document.querySelector("#page-banner");
	const pageBannerClose = document.querySelector("#page-banner-close");

	if (pageBanner) {
		// bouton de fermeture
		pageBannerClose.addEventListener("click", () => {
			document.body.classList.remove("page-with-banner");
		});
	}
}

// Activation auto du défilement horizontal
function bannerScrollingDetect() {
	const pageBanner = document.querySelector("#page-banner");

	if (pageBanner) {
		const bannerwrapperWidth = pageBanner.offsetWidth;
		const bannerTextWidth =
			pageBanner.querySelector(".banner__text").offsetWidth;
		if (bannerwrapperWidth > bannerTextWidth + 60) {
			pageBanner.classList.remove("page__banner--scrolling");
		} else {
			pageBanner.classList.add("page__banner--scrolling");
		}
	}
}

// Popup : Comportement au clic
function popupInit() {
	const popups = document.querySelectorAll("[data-popup]");

	popups.forEach((popup) => {
		popup.addEventListener("click", (event) => {
			// event.preventDefault();

			// ouvrir/fermer la popup
			const thisPopupName = popup.dataset.popup;
			if (thisPopupName) {
				const thisPopupEl = document.querySelector(
					`.page__popup#${thisPopupName}`
				);
				thisPopupEl.classList.toggle("page__popup--open");
			}

			// mettre les vidéos des popups en pause
			const htmlVideoFromPopup =
				document.querySelectorAll(".page__popup video");
			if (htmlVideoFromPopup.length > 0) {
				htmlVideoFromPopup.forEach((video) => {
					video.pause();
				});
			}
		});
	});

	document.addEventListener("mouseup", (event) => {
		// si une popup est ouverte
		if (
			document.querySelectorAll(".page__popup.page__popup--open").length > 0
		) {
			// click en dehors de la zone
			if (
				!document
					.querySelector(".page__popup .popup__wrapper")
					.contains(event.target) &&
				document.querySelector(".page__popup .popup__wrapper") !== event.target
			) {
				// fermer la popup
				document.querySelectorAll(".page__popup").forEach((popup) => {
					popup.classList.remove("page__popup--open");
				});

				// mettre les vidéos des popups en pause
				const htmlVideoFromPopup =
					document.querySelectorAll(".page__popup video");
				if (htmlVideoFromPopup.length > 0) {
					htmlVideoFromPopup.forEach((popupVideo) => {
						popupVideo.pause();
					});
				}
			}
		}
	});
}

// Toggle : Comportement au clic
function toggleInit() {
	const toggleListener = document.querySelectorAll("[data-toggle]");

	toggleListener.forEach((toggleButton) => {
		toggleButton.addEventListener("click", (event) => {
			// ouvrir/fermer l'accordéon
			let toggleData = toggleButton.dataset.toggle;
			if (toggleData) {
				toggleData = toggleData.split(",");
				let toggleTarget = toggleData.shift();
				if (toggleTarget) {
					// cible
					let toggleTargetEl = document.querySelector(`#${toggleTarget}`);
					if (toggleData) {
						toggleData.forEach(function (className) {
							className = className.trim();
							// ajouter/retirer les classes renseignés
							toggleTargetEl.classList.toggle(className);
						});
					} else {
						// ajouter/retirer la classe ".active"
						toggleTargetEl.classList.toggle("active");
					}
				}
			}
		});
	});
}

// Activation de Swiper.js (sliders)
function allSwiperInit() {
	if (
		document.querySelectorAll(
			".section-archive-related-news .swiper .swiper-slide"
		).length > 0
	) {
		const swiperRelatedNews = new Swiper(
			".section-archive-related-news .swiper",
			{
				// Core parameters
				slidesPerView: "auto",
				speed: 1200,
				resizeObserver: true,
				// Modules
				modules: [Autoplay, Scrollbar],
				// Modules parameters
				autoplay: {
					delay: 3600,
					disableOnInteraction: true,
				},
				scrollbar: {
					el: ".section-archive-related-news .swiper-scrollbar",
					draggable: true,
				},
			}
		);
	}

	if (
		document.querySelectorAll(
			".section-hero-single-portfolio .swiper .swiper-slide"
		).length > 0
	) {
		const swiperHeroSinglePortfolio = new Swiper(
			".section-hero-single-portfolio .swiper",
			{
				// Core parameters
				slidesPerView: "auto",
				speed: 1200,
				loop: true,
				resizeObserver: true,
				// Modules
				modules: [Autoplay, Navigation, Parallax],
				// Modules parameters
				autoplay: {
					delay: 2400,
					disableOnInteraction: true,
				},
				navigation: {
					nextEl: ".swiper-button-next",
					prevEl: ".swiper-button-prev",
				},
				parallax: true,
			}
		);
	}
	if (document.querySelectorAll(".excerpt-annonces").length > 0) {
		document.querySelectorAll(".excerpt-annonces").forEach(function (annonce) {
			const swiperHeroSinglePortfolio = new Swiper(
				annonce.querySelector(".swiper"),
				{
					// Core parameters
					slidesPerView: "auto",
					speed: 1200,
					loop: true,
					resizeObserver: true,
					// Modules
					modules: [Autoplay, Navigation, Parallax],
					// Modules parameters
					autoplay: {
						delay: 2400,
						disableOnInteraction: true,
					},
					navigation: {
						nextEl: annonce.querySelector(".swiper-button-next"),
						prevEl: annonce.querySelector(".swiper-button-prev"),
					},
					parallax: true,
				}
			);
		});
	}
}

// État du scrollSmoother plugin
function gsapScrollSmootherState(state) {
	if (scrollSmooth) {
		// Désactiver sur Élémentor
		if (document.querySelector("body.elementor-editor-active")) {
			scrollSmooth.kill();
		}
		// Rafraîchir sinon
		else {
			if (state === "refresh") {
				scrollSmooth.refresh();
			}
			// Mettre en pause
			if (state === "pause") {
				scrollSmooth.paused(true);
			}
			if (state === "play" && scrollSmooth.paused()) {
				scrollSmooth.paused(false);
			}
		}
	}
}

// Scroll vers une ancre
function gsapScrollTo() {
	// avec un [href="#..."]
	const scrollToAnchor = document.querySelectorAll("a[href^='#']");
	scrollToAnchor.forEach((link) => {
		link.addEventListener("click", (event) => {
			event.preventDefault();
			let anchorHref = link.getAttribute("href");
			scrollSmooth.scrollTo(anchorHref, true);
		});
	});

	// avec un [data-scrollto]
	const scrollToData = document.querySelectorAll("[data-scrollto]");
	scrollToData.forEach((data) => {
		data.addEventListener("click", () => {
			let anchorData = data.getAttribute("data-scrollto");
			if (anchorData == "auto") {
				anchorData = window.innerHeight + window.scrollY;
			}
			scrollSmooth.scrollTo(anchorData, true);
		});
	});

	// au chargement
	let hashInUrl = window.location.hash;
	if (hashInUrl) {
		scrollSmooth.scrollTo(hashInUrl, true);
	}
}

// Sticky element
function gsapStickyElement() {
	const stickyWrapper = document.querySelector("[data-sticky]");

	if (stickyWrapper) {
		ScrollTrigger.matchMedia({
			// desktop
			"(min-width: 992px)": () => {
				ScrollTrigger.create({
					trigger: stickyWrapper.firstElementChild,
					start: "center center",
					end: () =>
						`+=${
							stickyWrapper.offsetHeight -
							stickyWrapper.firstElementChild.offsetHeight
						}`,
					pin: true,
					pinSpacing: false,
					invalidateOnRefresh: true,
					// markers: true,
				});
			},
		});
	}
}

// Découpage des textes avec SplitText
function gsapSplitText() {
	textsToAnimated.forEach((_thisText) => {
		new SplitText(_thisText, {
			type: "lines, words", //, chars",
			linesClass: "line",
			wordsClass: "word",
			// charsClass: "letter",
		});
	});
}

// Animations avec le ScrollTrigger
function gsapAnimations() {
	gsap.to(".header__navbar__burger", { opacity: 1, y: 0 });

	// Ajout/retrait de la classe --animated
	elementsToAnimated.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			start: "-=100px +=80%",
			end: "bottom +=0%",
			onEnter: () => {
				setTimeout(() => {
					_this.classList.add("--animated");
				}, delay);
			},
			onLeaveBack: () => {
				_this.classList.remove("--animated");
			},
			// markers: true,
		});
	});

	// au chargement
	let hashInUrl = window.location.hash;
	if (hashInUrl) {
		scrollSmooth.scrollTo(hashInUrl, true);
	}

	// main title
	document.querySelectorAll(".main-title").forEach(function (title) {
		gsap.set(title.querySelectorAll("div span"), { y: "120%", opacity: 0 });
		gsap.to([title, title.querySelectorAll("div")], {
			x: 0,
			stagger: {
				each: 0.3,
				onStartParams: this,
				onStart: function (e) {
					if (this._targets[0].nodeName == "DIV") {
						gsap.to(this._targets[0].querySelector("span"), {
							y: 0,
							opacity: 1,
							delay: 0.5,
							ease: "Power3.easeOut",
						});
					}
				},
			},
			ease: "Power3.easeOut",
			duration: 0.8,
		});
	});

	if ( document.getElementById('breadcrumbs')) {
		gsap.to("#breadcrumbs", {
			opacity: 1,
			y: 0,
			delay: 1.5,
		});
	}
}

// Loader d'accueil
function gsapPreLoader() {
	// Chargement des fonctions GSAP préalables
	beforePreloader();

	// Lancement de l'animation de chargement
	let loaderTimeline = gsap.timeline();
	// Première visite
	if (
		!sessionStorage.getItem("visited") ||
		document.querySelector("body.home")
	) {
		loaderTimeline
			.set(document.querySelector(".page-transition div:last-child"), {
				x: "100%",
			})
			.set(document.querySelector(".page-transition div:first-child"), {
				x: "100%",
			})
			.call(gsapScrollSmootherState, ["pause"])
			.set(".sezam .logo", { y: "-100%", opacity: 0 })
			.set(".sezam .button-enter", { y: "100%", opacity: 0 })
			.to("#page-preloader-content", {
				delay: 0.3,
				duration: 0.6,
				ease: "power1.out",
				opacity: 1,
			})
			.to(
				"#page-preloader-wrapper",
				{
					delay: 0.3,
					duration: 0.6,
					ease: "power2.out",
					opacity: 0,
				},
				"<"
			)
			.to("#page-preloader", {
				visibility: "hidden",
			})
			.to(".sezam .logo", { y: "0", opacity: 1 });
		// after
		loaderTimeline.add(afterPreloader, loaderTimeline.duration() - 0.4);
		/* */
	}
	// Autres
	else {
		loaderTimeline
			// animation
			.set("#page-preloader-wrapper", {
				delay: 0.3,
				duration: 0.6,
				ease: "power2.out",
				opacity: 0,
			})
			.set("#page-preloader", {
				visibility: "hidden",
			})
			.to(document.querySelector(".page-transition div:last-child"), {
				x: "100%",
				ease: "Power2.easeOut",
				duration: 2,
			})
			.to(
				document.querySelector(".page-transition div:first-child"),
				{
					// light blue
					x: "100%",
					ease: "Power3.easeOut",
					delay: 0.2,
					duration: 2,
				},
				"<"
			);
		// after
		loaderTimeline.add(afterPreloader, loaderTimeline.duration() - 2);
	}
}

// Actions avant le Loader d'accueil
function beforePreloader() {
	// Chargement des fonctions GSAP préalables
	gsapSplitText();
	gsapStickyElement();
}

// Actions après le Loader d'accueil
function afterPreloader() {
	// Chargement des fonctions GSAP résultantes
	gsapScrollSmootherState("play");
	if (!document.querySelector(".button-enter")) gsapAnimations();
	gsapScrollTo();
}

if (document.querySelector(".sezam .button-enter")) {
	document.documentElement.style.overflowY = "hidden";
	if (scrollSmooth) {
		gsapScrollSmootherState("pause");
	}
	document
		.querySelector(".button-enter")
		.addEventListener("click", function (e) {
			e.preventDefault();
			gems.enter();
			gsapAnimations();
			gsap.to(".sezam", {
				opacity: 0,
				duration: 1,
				onComplete: function () {
					document.querySelector(".sezam").remove();

					document.documentElement.style.overflowY = "auto";
					if (scrollSmooth) {
						gsapScrollSmootherState("play");
					}
				},
			});
		});
}

const cameraTl = gsap.timeline({
	defaults: { ease: "none" },
	scrollTrigger: {
		trigger: ".animation-wrapper",
		start: "top top",
		end: "bottom top",
		scrub: true,
	},
});

let gemCamPosition = { y: 0 };
// successor
cameraTl.to(gemCamPosition, {
	y: -10,
	onUpdate: function () {
		gems.setCameraPosition(gemCamPosition.y);
	},
});

// become
cameraTl.to(gemCamPosition, {
	y: -20,
	onUpdate: function () {
		gems.setCameraPosition(gemCamPosition.y);
	},
});

// become
cameraTl.to(gemCamPosition, {
	y: -30,
	onUpdate: function () {
		gems.setCameraPosition(gemCamPosition.y);
	},
});

if (document.getElementById("contact-map")) {
	// load js
	var head = document.getElementsByTagName("head")[0];
	var js = document.createElement("script");
	js.type = "text/javascript";
	js.src = "https://unpkg.com/leaflet@1.6.0/dist/leaflet.js?ver=6.2.2";
	head.appendChild(js);

	// load css
	var head = document.getElementsByTagName("head")[0];
	var css = document.createElement("link");
	css.type = "text/css";
	css.rel = "stylesheet";
	css.href = "https://unpkg.com/leaflet@1.6.0/dist/leaflet.css?ver=6.2.2";
	head.appendChild(css);

	// load map
	setTimeout(function () {
		// Get datas
		var map = L.map("contact-map").setView(
			[49.114362468291894, 6.176760366374409],
			16
		);
		L.tileLayer("https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png", {
			attribution:
				'données © <a href="//osm.org/copyright">OpenStreetMap</a>/ODbL - rendu <a href="//openstreetmap.fr">OSM France</a>',
			minZoom: 1,
			maxZoom: 20,
		}).addTo(map);

		const markers = {
			agencies: [
				[
					346,
					"Office des Mosaïques",
					"11 Pl. Saint-Martin, 57000 Metz",
					"Metz",
					49.114362468291894,
					6.176760366374409,
				],
			],
		};
		var agencies = markers.agencies;
		for (var i = 0; i < agencies.length; i++) {
			var icon = L.icon({
				iconUrl: __webpack_public_path__ + "../public/img/marker.png",
				iconSize: [36, 48],
				iconAnchor: [18, 48],
				popupAnchor: [1, -53],
			});
			var marker = L.marker([agencies[i][4], agencies[i][5]], {
				icon: icon,
			}).addTo(map);
			/*marker.bindPopup(
        '<div class="map-box">'+
          '<div class="map-title">'+ agencies[i][1] +'</div>'+
          '<a href="'+document.location.origin+'/?page_id='+ agencies[i][0] +'" class="map-url">Voir l\'agence</a>'+
        '</div>'
      );*/
		}

		var resizeTimer;
		var mapIsZoomed;
		window.addEventListener("resize", function () {
			if (window.innerWidth < 768) {
				if (mapIsZoomed != true) {
					clearTimeout(resizeTimer);
					resizeTimer = setTimeout(function () {
						mapIsZoomed = true;
						map.setZoom(4);
					}, 100);
				}
			} else {
				clearTimeout(resizeTimer);
				resizeTimer = setTimeout(function () {
					mapIsZoomed = false;
					map.setZoom(6);
				}, 100);
			}
		});
	}, 1000);
}

document.querySelectorAll(".main-title").forEach(function (e) {
	gsap.set([e, e.querySelectorAll("div")], { x: "-100vw" });
});

if (document.querySelector(".explode")) {
	ScrollTrigger.create({
		trigger: ".explode",
		start: "top bottom",
		end: "bottom top",
		// markers: true,
		onToggle: (self) => {
			//console.log(self.isActive);
			if (self.isActive) gems.explode();
			else { gems.implode(); }
		},
		onUpdate: (self) => {
			gems.moveTriangles(self.progress.toFixed(3));
			// console.log(
			// 	"progress:",
			// 	self.progress.toFixed(3),
			// 	"direction:",
			// 	self.direction,
			// 	"velocity",
			// 	self.getVelocity()
			// );
		},
	});
}

window.onload = (event) => {
	if (document.querySelector(".page-transition")) {
		document.querySelectorAll("a:not([target=_blank])").forEach(function (e) {
			if (
				!e.href.includes("#") &&
				!e.classList.contains("elementor-accordion-title")
			) {
				e.addEventListener("click", function () {
					let transitionTl = gsap
						.timeline()
						.set(".page-transition", { opacity: 1 })
						.to(document.querySelector(".page-transition div:first-child"), {
							x: "0%",
							ease: "Power2.easeOut",
							duration: 0.3,
						})
						.to(
							document.querySelector(".page-transition div:last-child"),
							{
								// light blue
								x: "0%",
								ease: "Power3.easeOut",
								delay: 0.1,
								duration: 0.3,
							},
							"<"
						);
					setTimeout(function () {
						transitionTl.reverse();
					}, 1000);
				});
			}
		});
	}
};
